import { fieldTypeOf } from '../field-types';
import type { FieldTypeCard } from '../field-types/card';
import type { FieldElement } from '../format/form-definition';
import { messageOr } from './message';
import { matchesPattern } from './pattern';
import { isFilled, requiredText } from './required';
import { builtInCheckError } from './build-in-checks';
import { USER_PLACEHOLDERS } from '../texts/userPlaceholders';
import { rangeError } from './range';
import { repeatedRow, uniqueText } from './unique';
import type { ResolvedValidation } from './resolveValidations';

interface FieldCheck {
  field: FieldElement;
  value: unknown;
  validations: ResolvedValidation[];
  rowsAbove?: unknown[];
}

export const firstError = ({ field, value, validations, rowsAbove = [] }: FieldCheck): string | undefined => {
  const card = fieldTypeOf(field);
  if (!isFilled(card, value)) {
    const required = validations.find(validation => validation.kind === 'required');
    return required ? messageOr(required.message, requiredText(card.valueKind)) : undefined;
  }
  const builtIn = builtInCheckError(field, value);
  if (builtIn !== undefined) return builtIn;
  for (const validation of validations) {
    const error = defaultError(validation, card, value, rowsAbove);
    if (error !== undefined) return messageOr(validation.message, error);
  }
  return undefined;
};

const defaultError = (
  validation: ResolvedValidation,
  card: FieldTypeCard,
  value: NonNullable<unknown>,
  rowsAbove: unknown[],
): string | undefined => {
  switch (validation.kind) {
    case 'required':
      return undefined;
    case 'range':
      return rangeError(card, value, validation);
    case 'unique': {
      const row = repeatedRow(card, value, rowsAbove);
      return row === undefined ? undefined : uniqueText(card.valueKind, row);
    }
    case 'pattern':
      return typeof value !== 'string' || matchesPattern(validation.regex, value)
        ? undefined
        : USER_PLACEHOLDERS.pattern.invalid;
    default:
      return validation satisfies never;
  }
};
